import { Injectable } from '@angular/core';
import {
  HttpContext,
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { map, Observable } from 'rxjs';
import {
  formatLegacyDate,
  formatLegacyDateTime,
  formatLegacyDateOnlyTime,
  isIsoDateOrDateTime,
  parseLegacyDate,
  parseLegacyDateTime,
} from './legacy-date-converter';

export type LegacyDateType = 'date' | 'date-time' | 'date-only' | 'none';

export interface LegacyDateNormalizationOptions {
  /**
   * Per-field type definition:
   * - 'date' forces date-only semantics (but time being parsed and sent as 00:00:00 UTC in http requests)
   * - 'date-time' preserves hours/min/sec
   * - 'date-only' forces date-only semantics (only yyyy-MM-dd is parsed and sent in http requests)
   */
  fields?: Record<string, LegacyDateType>;
  /**
   * Default conversion to apply for fields not listed in `fields`:
   * - 'date' → treat unlisted fields as date (default - )
   * - 'date-time' → treat unlisted fields as date-time
   * - 'none' → do not convert unlisted fields (developer will post-process)
   */
  defaultConversion?: LegacyDateType;
}

export const LEGACY_DATE_OPTIONS = new HttpContextToken<LegacyDateNormalizationOptions | null>(() => null);

interface ReqBody {
  [key: string]: unknown;
}

/**
 * Sample usage:
 *
 * @example
 * return this.httpClient.get(url, {
 *   withCredentials: true,
 *   context: legacyDates(),
 * });
 */
export function legacyDates(options: LegacyDateNormalizationOptions = {}): HttpContext {
  return new HttpContext().set(LEGACY_DATE_OPTIONS, options);
}

export function withLegacyDates(options: LegacyDateNormalizationOptions = {}): (ctx: HttpContext) => HttpContext {
  return (ctx: HttpContext) => ctx.set(LEGACY_DATE_OPTIONS, options);
}

/**
 * Intercepteur gérant les données de type date et datetime retournées par les services REST legacy
 * (qui ne distinguent pas les types date et datetime).
 *
 * Au moment de la requête (sens outgoing), cet intercepteur transforme toute instance Date
 * dans le requestBody au format RFC 3339 avec la composante time forcée à 00:00:00 et à la timezone UTC.
 *
 * Au moment de la réponse (sens incoming), cet intercepteur transforme tout datetime (donnée au format RFC 3339 sans composante time)
 * et date (donnée au format RFC 3339 avec composante time) en instance Date à la timezone locale du navigateur.
 * Il force la composante time à 00:00:00 et ne prendra en compte que les jours/mois/années (en ignorant la timezone).
 *
 * Note sur le comportement des services REST legacy :
 *
 * - Si la donnée est une date (pas de composante heure), alors la donnée échangée est une datetime RFC 3339
 *   avec la timezone UTC et à 00:00:00.
 * - Si la donnée est une datetime, alors la donnée échangée est une datetime RFC 3339.
 *
 * Note sur une potentielle évolution de ce composant :
 * Si le fait de forcer la composante time à 00:00:00 pose un problème, on pourra faire évoluer ce composant
 * pour pouvoir indiquer quelles sont les propriétés de type date (sans composante time) et les propriétés de type datetime
 * (avec composante time).
 */
@Injectable()
export class LegacyDateNormalizationInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<ReqBody>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const options = request.context.get(LEGACY_DATE_OPTIONS);

    if (!options) return next.handle(request);

    // create resolvedTypes dictionary (optimization to avoid evaluating the same keys multiple times)
    const resolvedTypes = new Map<string | null, LegacyDateType | undefined>();
    const resolveType = (key: string | null) =>
      resolvedTypes.get(key) ?? resolvedTypes.set(key, this.resolveTypeForCurrentField(key, options)).get(key);

    const maybeModified = this.convertOutgoingDates(request.body, resolveType);
    const modifiedReq = maybeModified === request.body ? request : request.clone({ body: maybeModified });

    return next.handle(modifiedReq).pipe(
      map(event => {
        if (event instanceof HttpResponse && this.isJsonResponse(event)) {
          return event.clone({ body: this.convertIncomingDates(event.body, resolveType) });
        }
        return event;
      }),
    );
  }

  convertOutgoingDates(value: unknown, resolveType: (key: string | null) => LegacyDateType | undefined) {
    return this.traverse(
      value,
      v => v instanceof Date,
      (v, k) => this.formatDate(v as Date, resolveType(k)),
    );
  }

  convertIncomingDates(value: unknown, resolveType: (key: string | null) => LegacyDateType | undefined) {
    return this.traverse(
      value,
      v => isIsoDateOrDateTime(v),
      (v, k) => this.parseDate(v as string, resolveType(k)),
    );
  }

  private traverse(
    value: unknown,
    predicate: (v: unknown, key: string | null) => boolean,
    transform: (v: unknown, key: string | null) => unknown,
    key: string | null = null,
  ): unknown {
    if (value == null || value instanceof FormData) return value;

    if (predicate(value, key)) return transform(value, key);

    if (Array.isArray(value)) {
      return value.map((v, i) => this.traverse(v, predicate, transform, String(i)));
    }

    if (this.isRecord(value)) {
      const result: Record<string, unknown> = {};
      let changed = false;

      for (const [k, v] of Object.entries(value)) {
        const transformed = this.traverse(v, predicate, transform, k);
        result[k] = transformed;
        if (transformed !== v) changed = true;
      }

      return changed ? result : value;
    }

    return value;
  }

  private parseDate(value: string, kind: LegacyDateType | undefined) {
    switch (kind) {
      case 'date':
        return parseLegacyDate(value);
      case 'date-time':
        return parseLegacyDateTime(value);
      case 'date-only':
        return parseLegacyDate(value); // parseLegacyDate also parses yyyy-MM-dd
      default:
        return value;
    }
  }

  private formatDate(value: Date, kind: LegacyDateType | undefined) {
    switch (kind) {
      case 'date':
        return formatLegacyDate(value);
      case 'date-time':
        return formatLegacyDateTime(value);
      case 'date-only':
        return formatLegacyDateOnlyTime(value);
      default:
        return value;
    }
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private isJsonResponse(response: HttpResponse<unknown>): boolean {
    const contentType = response.headers.get('Content-Type') || '';
    return contentType.toLowerCase().includes('application/json');
  }

  /**
   * @param field name of the field
   * @param options conversion options
   * Returns
   * - LegacyDateKind the resolved type
   * - undefined if the type must not be converted
   */
  private resolveTypeForCurrentField(
    field: string | null,
    options: LegacyDateNormalizationOptions,
  ): LegacyDateType | undefined {
    const mapping = options.fields ?? {};
    const defaultKind: LegacyDateType = options.defaultConversion ?? 'date';

    if (field == null) return defaultKind === 'none' ? undefined : defaultKind;

    const kind = mapping[field];
    return kind === 'none' ? undefined : (kind ?? (defaultKind === 'none' ? undefined : defaultKind));
  }
}
